import { useContext, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import moment from 'moment';
import dayjs from 'dayjs';
import 'dayjs/locale/es';
import 'moment/locale/es';
import { LoadersContext } from '_models/context/app';

export const useSalesExecutiveHook = ({
  getMySalesExecutiveDetails,
  cleanMySalesAction,
  mySalesExecutive,
  getMySalesExecutive,
}) => {
  const loadersContext = useContext(LoadersContext);
  const { setIsScreenLoading } = loadersContext || {};

  dayjs.locale('es');

  const currentDate = dayjs();
  const firstDayOfMonth = currentDate.startOf('month');
  const [isLoading, setIsLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState();
  const [options, setOptions] = useState([]);
  const [dropdownValue, setDropdownValue] = useState('');
  const [startValue, setStartValue] = useState(firstDayOfMonth);
  const [endValue, setEndValue] = useState(currentDate);
  const [fromTime, setFromTime] = useState(firstDayOfMonth.format());
  const [toTime, setToTime] = useState(currentDate.format());
  const [data, setData] = useState([]);
  const [displayDatepickers, setDisplayDatepickers] = useState('displayDatepickers hidden');
  const [render, setRender] = useState(true);
  const [dayDate, setDayDate] = useState('');
  const location = useLocation();
  const history = useNavigate();

  const queryParameters = new URLSearchParams(window.location.search);
  const fromTimeParameters = queryParameters.get('from_time');
  const toTimeParameters = queryParameters.get('to_time');
  const daySelected = queryParameters.get('date');

  useEffect(() => {
    if (fromTimeParameters !== null) {
      const fromDate = dayjs(fromTimeParameters);
      const toDate = dayjs(toTimeParameters);

      const areNotFirstAndLastDay =
        !fromDate.isSame(firstDayOfMonth, 'day') ||
        !toDate.isSame(currentDate.endOf('month'), 'day');

      setFromTime(fromTimeParameters);
      setToTime(toTimeParameters);

      if (areNotFirstAndLastDay) {
        setDisplayDatepickers('displayDatepickers shown');
        setStartValue(fromDate);
        setEndValue(toDate);
      } else {
        setDisplayDatepickers('displayDatepickers hidden');
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location, fromTimeParameters, toTimeParameters, daySelected]);

  useEffect(() => {
    populateDropdown();
    const urlContent = window.location.href;

    if (urlContent.includes('from_time')) {
      const urlSplit = urlContent.split('?')[1];
      setFromTime(urlSplit.split('&')[0].split('from_time=')[1]);
      setToTime(urlSplit.split('&')[1].split('to_time=')[1]);
      setStartValue(dayjs(urlSplit.split('&')[0].split('from_time=')[1]));
      setEndValue(dayjs(urlSplit.split('&')[1].split('to_time=')[1]));
    }

    return () => {
      cleanMySalesAction();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (location?.search === '' && !render) {
      if (
        fromTime.split('-')[2] !== '01T00:00:00' ||
        toTime.split('-')[2].split('T')[1] !== '23:59:59'
      ) {
        setDisplayDatepickers('displayDatepickers shown');
      }
      if (dropdownValue === 1) {
        getMySalesMethod();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fromTime, toTime]);

  useEffect(() => {
    if (fromTime && toTime && render) {
      setRender(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (dropdownValue !== null && dropdownValue !== '' && !render) {
      setRender(false);
      if (dropdownValue !== 1) {
        getMySalesMethod();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dropdownValue]);

  useEffect(() => {
    const dataArray = [];
    dataArray.push(['Fecha', '# Venta diaria']);
    const { payload } = mySalesExecutive || {};
    const { result } = payload || {};
    if (mySalesExecutive?.loaded) {
      setIsLoading(false);
      setRows(payload);
      if (result?.length > 0) {
        result?.forEach((item) => {
          dataArray.push([item?.date || 0, item?.total_sales]);
        });
      }
    }
    setData(dataArray);
  }, [mySalesExecutive]);

  useEffect(() => {
    if (dayDate !== '') {
      getMySalesExecutiveDetails({
        date: dayDate,
      });
      history(`/sales/ejecutivo/details?date=${dayDate}`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dayDate, history]);

  const getMySalesMethod = () => {
    setIsLoading(true);
    const fromTimeFormatted = fromTime ? dayjs(fromTime).format('YYYY-MM-DD') : '';
    const toTimeFormatted = toTime ? dayjs(toTime).format('YYYY-MM-DD') : '';

    const queryParams = `from_time=${fromTimeFormatted}&to_time=${toTimeFormatted}`;
    const newUrl = `/sales/ejecutivo?${queryParams}`;

    window.history.pushState(null, '', newUrl);

    getMySalesExecutive({
      from_time: fromTimeFormatted,
      to_time: toTimeFormatted,
    });
  };

  const handleClose = () => {
    setOpen(false);
    setIsScreenLoading(false);
  };

  const handleMySalesDetails = (param) => {
    setDayDate(param);
  };

  const populateDropdown = () => {
    const myArray = [];

    for (let i = 0; i <= 5; i++) {
      const forPreviousID = moment().subtract(i, 'months').format('YYYY-MM');

      const forPreviousName = moment().subtract(i, 'months').format('MMMM');

      const forPreviousMonth = moment().subtract(i, 'months').format('MM');

      const forPreviousYear = moment().subtract(i, 'months').format('YYYY');

      myArray.push({
        id: forPreviousID,
        name: forPreviousName,
        month: forPreviousMonth,
        year: forPreviousYear,
      });
    }

    // Obtener el mes de las fechas en la URL
    const urlContent = window.location.href;

    if (urlContent.includes('?')) {
      const urlSplit = urlContent.split('?')[1];
      const queryParams = urlSplit.split('&');

      let urlMonth = null;

      queryParams.forEach((param) => {
        const [key, value] = param.split('=');
        if (key === 'from_time') {
          urlMonth = moment(value).format('YYYY-MM');
        }
      });

      // Comprobar si el mes de la URL está en myArray
      const urlMonthExistsInArray = myArray.some((option) => option.id === urlMonth);

      if (urlMonthExistsInArray) {
        // Si el mes de la URL existe en myArray, establecer dropdownValue en ese mes
        setDropdownValue(urlMonth);
      } else {
        // Si no existe, establecer dropdownValue en "Personalizado"
        setDropdownValue('Personalizado');
      }
    } else {
      // Si no se encuentran parámetros en la URL, establecer dropdownValue en el mes actual
      setDropdownValue(moment().format('YYYY-MM'));
    }

    setOptions(myArray);
  };

  const inputChange = ({ target }) => {
    const { value } = target || {};

    if (value === 1) {
      setDisplayDatepickers('displayDatepickers shown');
      setEndValue(currentDate);
      setStartValue(dayjs(moment().subtract(1, 'day').format('YYYY-MM-DD')));
    } else if (value === 0) {
      setDisplayDatepickers('displayDatepickers hidden');
      setFromTime(moment().startOf('month').format());
      setToTime(moment().add(1, 'months').startOf('month').format());

      setStartValue(dayjs(moment().startOf('month').format('MM-DD-YYYY')));
      setEndValue(dayjs(moment().endOf('month').format('MM-DD-YYYY')));
    } else {
      setDisplayDatepickers('displayDatepickers hidden');
      setFromTime(
        moment(value + '-01')
          .startOf('month')
          .format(),
      );
      setToTime(
        moment(value)
          // .add(1, "months")
          .endOf('month')
          .format(),
      );

      setStartValue(
        dayjs(
          moment(value + '-01')
            .startOf('month')
            .format('MM-DD-YYYY'),
        ),
      );
      setEndValue(
        dayjs(
          moment(value + '-01')
            .endOf('month')
            .format('MM-DD-YYYY'),
        ),
      );
    }

    setDropdownValue(value);
  };

  return {
    isLoading,
    rows,
    options,
    dropdownValue,
    handleClose,
    data,
    inputChange,
    handleMySalesDetails,
    displayDatepickers,
    startValue,
    setStartValue,
    setDropdownValue,
    setFromTime,
    open,
    endValue,
    setEndValue,
    setToTime,
  };
};
